import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, RefreshCw, ExternalLink, Truck, Banknote, Image as ImageIcon } from 'lucide-react';

const STATUS_OPTIONS = ['requested', 'approved', 'picked_up', 'received', 'refunded', 'rejected'];
const STATUS_BADGE = {
  requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  picked_up: 'bg-indigo-100 text-indigo-800',
  received: 'bg-purple-100 text-purple-800',
  refunded: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const fmtINR = (n) => `₹${Number(n || 0).toFixed(2)}`;

const AdminReturns = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [savingId, setSavingId] = useState('');
  const [refunding, setRefunding] = useState(null); // return object
  const [refundAmount, setRefundAmount] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchReturns(); }, []);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/admin/returns`, { withCredentials: true });
      setReturns(data.returns || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => {
    if (filter === 'all') return returns;
    return returns.filter(r => r.status === filter);
  }, [returns, filter]);

  const patchReturn = async (returnId, payload) => {
    setSavingId(returnId);
    try {
      const { data } = await axios.patch(
        `${apiBase}/api/admin/returns/${returnId}`,
        payload,
        { withCredentials: true }
      );
      setReturns((prev) => prev.map(r => (r.return_id === returnId ? { ...r, ...data } : r)));
      if (data?._message) toast.success(data._message);
      else toast.success('Updated');
      // refresh counts
      fetchReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId('');
    }
  };

  const triggerReversePickup = async (ret) => {
    setSavingId(ret.return_id);
    try {
      const { data } = await axios.post(
        `${apiBase}/api/admin/returns/${ret.return_id}/reverse-pickup`,
        {},
        { withCredentials: true }
      );
      toast.success(`Reverse AWB generated: ${data.reverse_awb}`);
      fetchReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reverse pickup failed');
    } finally {
      setSavingId('');
    }
  };

  const openRefund = (ret) => {
    setRefunding(ret);
    setRefundAmount(String(ret.order_total || 0));
  };

  const submitRefund = async () => {
    if (!refunding) return;
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) {
      toast.error('Refund amount must be greater than zero.');
      return;
    }
    if (amt > Number(refunding.order_total) + 0.01) {
      toast.error(`Cannot exceed original order total (${fmtINR(refunding.order_total)})`);
      return;
    }
    setSubmittingRefund(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/api/admin/returns/${refunding.return_id}/refund`,
        { amount: amt },
        { withCredentials: true }
      );
      toast.success(`Refund processed (${fmtINR(data.refund_amount)})`);
      setRefunding(null);
      setRefundAmount('');
      fetchReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setSubmittingRefund(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-3xl font-black font-heading" data-testid="returns-heading">Returns</h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReturns} data-testid="refresh-returns">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Counters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[['all', 'All'], ...STATUS_OPTIONS.map(s => [s, s.replace('_', ' ')])].map(([key, label]) => {
            const cnt = key === 'all' ? (counts.total || returns.length) : (counts[key] || 0);
            const active = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? 'bg-primary text-white border-primary' : 'bg-white border-[#E0E0E0] hover:border-primary/40'}`}
                data-testid={`filter-${key}`}
              >
                <span className="capitalize">{label}</span>
                <span className="ml-1.5 opacity-70">({cnt})</span>
              </button>
            );
          })}
        </div>

        {visible.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="returns-empty">
            No returns in this view.
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((ret) => (
              <div
                key={ret.return_id}
                className="p-5 rounded-2xl bg-white border border-[#E0E0E0]"
                data-testid={`return-row-${ret.return_id}`}
              >
                <div className="flex flex-col lg:flex-row gap-5">
                  {/* Photo */}
                  <div className="lg:w-32">
                    {ret.photo_url ? (
                      <button
                        type="button"
                        className="block w-full aspect-square rounded-lg overflow-hidden border border-[#E0E0E0] hover:border-primary"
                        onClick={() => setPhotoPreview(`${apiBase}${ret.photo_url}`)}
                        data-testid={`return-photo-thumb-${ret.return_id}`}
                      >
                        <img
                          src={`${apiBase}${ret.photo_url}`}
                          alt="return"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ) : (
                      <div className="w-full aspect-square rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Details + controls */}
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm text-muted-foreground">{ret.return_id}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE[ret.status] || 'bg-gray-100 text-gray-800'}`} data-testid={`return-status-${ret.return_id}`}>
                            {ret.status?.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="font-bold mt-0.5">
                          Order <span className="text-primary">#{ret.order_id}</span> · {fmtINR(ret.order_total)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {ret.user_name || 'Guest'} · {ret.user_email}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {ret.created_at ? new Date(ret.created_at).toLocaleString('en-IN') : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {ret.status !== 'refunded' && ret.status !== 'rejected' && (
                          <>
                            {!ret.reverse_awb && ret.status !== 'requested' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => triggerReversePickup(ret)}
                                disabled={savingId === ret.return_id}
                                data-testid={`reverse-pickup-${ret.return_id}`}
                              >
                                <Truck className="h-4 w-4 mr-1" /> Reverse Pickup
                              </Button>
                            )}
                            {['approved', 'received'].includes(ret.status) && ret.razorpay_payment_id && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => openRefund(ret)}
                                data-testid={`open-refund-${ret.return_id}`}
                              >
                                <Banknote className="h-4 w-4 mr-1" /> Process Refund
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-sm bg-amber-50/40 border border-amber-100 rounded-lg p-3">
                      <span className="font-semibold">Reason:</span> {ret.reason}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Status</Label>
                        <Select
                          value={ret.status || 'requested'}
                          onValueChange={(v) => patchReturn(ret.return_id, { status: v })}
                          disabled={savingId === ret.return_id || ret.status === 'refunded'}
                        >
                          <SelectTrigger data-testid={`status-select-${ret.return_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => (
                              <SelectItem key={s} value={s} data-testid={`status-option-${s}-${ret.return_id}`}>{s.replace('_', ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Reverse AWB</Label>
                        <div className="flex gap-1">
                          <Input
                            defaultValue={ret.reverse_awb || ''}
                            placeholder="e.g. 1234567890"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (ret.reverse_awb || '')) patchReturn(ret.return_id, { reverse_awb: v });
                            }}
                            data-testid={`awb-input-${ret.return_id}`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Refund ID</Label>
                        <div className="text-sm py-2 px-2 bg-muted rounded font-mono truncate" data-testid={`refund-id-${ret.return_id}`}>
                          {ret.refund_id ? (
                            <>
                              {ret.refund_id} {ret.refund_amount ? `(${fmtINR(ret.refund_amount)})` : ''}
                            </>
                          ) : '—'}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Admin notes</Label>
                      <Input
                        defaultValue={ret.admin_notes || ''}
                        placeholder="Internal notes / reason for rejection…"
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== (ret.admin_notes || '')) patchReturn(ret.return_id, { admin_notes: v });
                        }}
                        data-testid={`notes-input-${ret.return_id}`}
                      />
                    </div>

                    <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                      <a
                        href={`/admin/orders`}
                        className="inline-flex items-center hover:text-primary"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" /> View in orders
                      </a>
                      {ret.razorpay_payment_id && (
                        <span>Payment: <code>{ret.razorpay_payment_id}</code></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refund dialog */}
      <Dialog open={!!refunding} onOpenChange={(open) => { if (!open && !submittingRefund) { setRefunding(null); setRefundAmount(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Process refund</DialogTitle>
            <DialogDescription>
              Refund will be issued via Razorpay against the original payment.
              The customer typically receives it in 5–7 business days.
            </DialogDescription>
          </DialogHeader>

          {refunding && (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-100 bg-green-50/40 p-3 text-sm">
                <div><b>Order:</b> #{refunding.order_id}</div>
                <div><b>Original total:</b> {fmtINR(refunding.order_total)}</div>
                <div><b>Razorpay payment:</b> <code className="text-xs">{refunding.razorpay_payment_id || '—'}</code></div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="refund-amount">Refund amount (₹) <span className="text-red-600">*</span></Label>
                <Input
                  id="refund-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  max={refunding.order_total}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="refund-amount-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  Max {fmtINR(refunding.order_total)}. Set lower to retain a restocking fee.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setRefunding(null); setRefundAmount(''); }} disabled={submittingRefund} data-testid="refund-cancel">
              Cancel
            </Button>
            <Button onClick={submitRefund} disabled={submittingRefund} className="bg-green-600 hover:bg-green-700" data-testid="refund-confirm">
              {submittingRefund ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing…</>) : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo preview */}
      <Dialog open={!!photoPreview} onOpenChange={(open) => { if (!open) setPhotoPreview(''); }}>
        <DialogContent className="sm:max-w-2xl p-2" data-testid="photo-preview-dialog">
          {photoPreview && (
            <img src={photoPreview} alt="return evidence" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReturns;
